import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import LoadingCard from "components/LoadingCard/LoadingCard";
import ListSearch from "components/ListSearch/ListSearch";
import { useTextFilter } from "helpers/textFilter";
import { CONTROL } from "pages/MarketingPages/FieldInput";
import { uploadImage } from "pages/MarketingPages/pagesApi";
import { swapPatches } from "./testimonialOrder";
import {
  createTestimonial,
  deleteTestimonial,
  errorMessage,
  fetchTestimonials,
  setTestimonialStatus,
  updateTestimonial,
} from "./testimonialsApi";

const { Title, Text, Paragraph } = Typography;

const testimonialSearchFields = (testimonial) => [testimonial.pet, testimonial.author, testimonial.text];

/**
 * Customer testimonials on the landing site.
 *
 * These are real customers' verbatim words, so the operations that matter are
 * adding new quotes and reordering the row — not rewriting old ones. The edit
 * form therefore keeps the quote text locked behind an explicit unlock with a
 * warning; everything else (pet, author, date, photo) edits freely. There is
 * no language switch: quotes are never translated.
 */
export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [isTextLocked, setIsTextLocked] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      // Already in site order — the API sorts by `order`, then newest.
      setTestimonials(await fetchTestimonials());
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isNew = editing != null && !editing._id;

  const openEditor = (testimonial) => {
    setEditing(testimonial ?? {});
    // Existing quotes open with the text read-only; a new one types freely.
    setIsTextLocked(Boolean(testimonial));
    form.setFieldsValue({
      pet: testimonial?.pet ?? "",
      author: testimonial?.author ?? "",
      date: testimonial?.date ? dayjs(testimonial.date) : null,
      photo: testimonial?.photo ?? "",
      text: testimonial?.text ?? "",
    });
  };

  const closeEditor = () => {
    setEditing(null);
    setIsTextLocked(false);
    form.resetFields();
  };

  const submit = async (values) => {
    setIsSaving(true);
    try {
      const payload = {
        pet: values.pet,
        author: values.author || "",
        date: values.date.format("YYYY-MM-DD"),
        photo: values.photo || "",
        text: values.text,
      };

      if (isNew) {
        // No `order` in the payload: the API appends new quotes to the end of
        // the row (max+1), so every writer gets the same behaviour.
        await createTestimonial(payload);
      } else {
        await updateTestimonial(editing._id, payload);
      }

      message.success("Запазено");
      closeEditor();
      await load();
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const changeStatus = async (testimonial, status) => {
    try {
      await setTestimonialStatus(testimonial._id, status);
      message.success(status === "published" ? "Публикувано на сайта" : "Свалено от сайта");
      await load();
    } catch (error) {
      message.error(errorMessage(error));
    }
  };

  const remove = async (testimonial) => {
    try {
      await deleteTestimonial(testimonial._id);
      message.success("Изтрито");
      await load();
    } catch (error) {
      message.error(errorMessage(error));
    }
  };

  const visibleTestimonials = useTextFilter(testimonials, testimonialSearchFields, searchQuery);
  // Moving a row while the list is filtered would swap it with a neighbour the
  // admin can't see, so reordering waits until the search is cleared (as FAQ).
  const isSearchActive = Boolean(searchQuery);

  const move = async (testimonial, direction) => {
    const patches = swapPatches(testimonials, testimonial._id, direction);
    if (!patches.length) return;
    try {
      // The two patches target distinct rows, so they can run together.
      await Promise.all(patches.map((patch) => updateTestimonial(patch.id, { order: patch.order })));
      await load();
    } catch (error) {
      message.error(errorMessage(error));
      await load();
    }
  };

  const uploadPhoto = async (file) => {
    setIsUploading(true);
    try {
      const url = await uploadImage(file);
      if (!url) throw new Error("Качването не върна URL адрес");
      form.setFieldsValue({ photo: url });
    } catch (error) {
      message.error(`Неуспешно качване: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 720, margin: "0 auto" }}>
      <Space direction="vertical" size={4} style={{ width: "100%", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Отзиви
        </Title>
        <Text type="secondary">
          Отзиви на клиенти на сайта, показани в този ред. Това са реалните думи на клиентите,
          затова добавяй и подреждай свободно — самият текст на отзива остава заключен при
          съществуващите записи.
        </Text>
      </Space>

      <Button
        type="primary"
        block
        style={{ ...CONTROL, marginBottom: 16 }}
        onClick={() => openEditor(null)}
      >
        Нов отзив
      </Button>

      {isLoading && <LoadingCard />}

      {!isLoading && !loadError && (
        <ListSearch
          value={searchQuery}
          onSearch={setSearchQuery}
          placeholder="Търсене по автор или текст"
          className="mb-3"
        />
      )}

      {!isLoading && !loadError && isSearchActive && (
        <Text type="secondary" className="d-block mb-3">
          Изчистете търсенето, за да пренареждате.
        </Text>
      )}

      {!isLoading && loadError && (
        <Alert
          type="error"
          showIcon
          message="Неуспешно зареждане на отзивите"
          description={loadError}
          action={<Button onClick={load}>Опитай отново</Button>}
        />
      )}

      {!isLoading && !loadError && (
        <List
          bordered
          dataSource={visibleTestimonials}
          locale={{
            emptyText: searchQuery ? "Няма отзиви, отговарящи на търсенето." : "Все още няма отзиви.",
          }}
          renderItem={(testimonial, index) => (
            <List.Item style={{ display: "block", padding: 16 }}>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Space wrap size={8}>
                  {testimonial.status === "published" ? (
                    <Tag color="green">Публикувано</Tag>
                  ) : (
                    <Tag>Чернова</Tag>
                  )}
                  {testimonial.legacy && <Tag color="purple">Стар</Tag>}
                  <Text type="secondary">
                    {testimonial.date ? dayjs(testimonial.date).format("D MMM YYYY") : ""}
                  </Text>
                </Space>

                <Text strong>
                  {testimonial.pet}
                  {testimonial.author ? ` — ${testimonial.author}` : ""}
                </Text>
                <Paragraph style={{ margin: 0 }} ellipsis={{ rows: 3 }}>
                  {testimonial.text}
                </Paragraph>

                <Space wrap size={8}>
                  <Button
                    style={CONTROL}
                    icon={<ArrowUpOutlined />}
                    aria-label="Премести нагоре"
                    disabled={isSearchActive || index === 0}
                    onClick={() => move(testimonial, "up")}
                  />
                  <Button
                    style={CONTROL}
                    icon={<ArrowDownOutlined />}
                    aria-label="Премести надолу"
                    disabled={isSearchActive || index === testimonials.length - 1}
                    onClick={() => move(testimonial, "down")}
                  />
                  <Button style={CONTROL} onClick={() => openEditor(testimonial)}>
                    Редактирай
                  </Button>
                  {testimonial.status === "published" ? (
                    <Button style={CONTROL} onClick={() => changeStatus(testimonial, "draft")}>
                      Свали от сайта
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      style={CONTROL}
                      onClick={() => changeStatus(testimonial, "published")}
                    >
                      Публикувай
                    </Button>
                  )}
                  <Popconfirm
                    title="Сигурен ли си, че искаш да изтриеш този отзив?"
                    onConfirm={() => remove(testimonial)}
                    okText="Да"
                    cancelText="Отказ"
                  >
                    <Button danger style={CONTROL}>
                      Изтрий
                    </Button>
                  </Popconfirm>
                </Space>
              </Space>
            </List.Item>
          )}
        />
      )}

      <Modal
        open={editing != null}
        title={isNew ? "Нов отзив" : "Редактиране на отзив"}
        onCancel={closeEditor}
        onOk={form.submit}
        confirmLoading={isSaving}
        // "Запази", never "Публикувай" — the list's Publish button is the only way live.
        okText="Запази"
        cancelText="Отказ"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={submit} requiredMark={false}>
          <Form.Item
            name="pet"
            label="Домашен любимец"
            rules={[{ required: true, whitespace: true, message: "Името на домашния любимец е задължително" }]}
          >
            <Input style={CONTROL} />
          </Form.Item>

          <Form.Item name="author" label="Автор (незадължително)">
            <Input style={CONTROL} />
          </Form.Item>

          <Form.Item
            name="date"
            label="Дата"
            rules={[{ required: true, message: "Отзивът се нуждае от дата" }]}
          >
            <DatePicker style={{ ...CONTROL, width: "100%" }} />
          </Form.Item>

          <Form.Item name="photo" label="Снимка (незадължително)">
            <Input
              style={CONTROL}
              placeholder="/images/…"
              addonAfter={
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    uploadPhoto(file);
                    return false;
                  }}
                >
                  <Button size="small" type="text" icon={<UploadOutlined />} loading={isUploading}>
                    Качи
                  </Button>
                </Upload>
              }
            />
          </Form.Item>

          <Form.Item
            name="text"
            label="Цитат"
            rules={[{ required: true, whitespace: true, message: "Текстът на отзива е задължителен" }]}
            extra={
              isTextLocked ? (
                // The deliberate friction of this screen: existing quotes are a
                // customer's verbatim words, so editing them takes an explicit
                // unlock — adding and reordering are the operations that matter.
                <Space direction="vertical" size={4} style={{ marginTop: 4 }}>
                  <Text type="warning">
                    Това са думите на клиента — редактирайте само при правописна грешка.
                  </Text>
                  <Button style={CONTROL} onClick={() => setIsTextLocked(false)}>
                    Редактирай текста на отзива
                  </Button>
                </Space>
              ) : undefined
            }
          >
            <Input.TextArea rows={4} disabled={isTextLocked} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
